import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CheckCircle, CrownSimple, WhatsappLogo, Sparkle, Ticket, Clock } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Pricing() {
  const [plans, setPlans] = useState([]);
  const [sub, setSub] = useState(null);
  const [requests, setRequests] = useState([]);
  const [dialog, setDialog] = useState(null);
  const [sending, setSending] = useState(false);
  const [coupon, setCoupon] = useState("");
  const [redeeming, setRedeeming] = useState(false);

  const load = () => {
    api.get("/plans").then((r) => setPlans(r.data));
    api.get("/subscriptions/me").then((r) => setSub(r.data.subscription));
    api.get("/subscriptions/my-requests").then((r) => setRequests(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const pendingFor = (planId) => requests.find((r) => r.plan_id === planId && r.status === "pending");

  const requestPlan = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/subscriptions/request", { plan_id: dialog.id });
      window.open(data.whatsapp_link, "_blank");
      setDialog(null);
      load();
      toast.success("Demande enregistrée ! Finalisez le paiement sur WhatsApp — l'administrateur activera votre abonnement.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSending(false); }
  };

  const redeem = async () => {
    if (!coupon.trim()) { toast.error("Saisissez un code."); return; }
    setRedeeming(true);
    try {
      const { data } = await api.post("/coupons/redeem", { code: coupon });
      setSub(data.subscription);
      setCoupon("");
      toast.success(data.message);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setRedeeming(false); }
  };

  return (
    <div className="space-y-8">
      <div className="animate-fade-up">
        <h1 className="font-display text-3xl font-extrabold sm:text-4xl">Abonnement</h1>
        <p className="mt-1 text-ink/65">Choisissez votre offre, payez par Mobile Money via WhatsApp, et l'administrateur active votre accès.</p>
      </div>

      {sub && (
        <div className="flex items-center justify-between rounded-2xl border border-forest/30 bg-forest/5 p-5" data-testid="active-sub">
          <div className="flex items-center gap-3">
            <div className="grid h-10 w-10 place-items-center rounded-xl bg-forest/15 text-forest"><CheckCircle weight="fill" size={22} /></div>
            <div>
              <div className="font-semibold">Abonnement {sub.plan_name} actif</div>
              <div className="text-sm text-muted-foreground">Expire le {new Date(sub.expires_at).toLocaleDateString("fr-FR")}</div>
            </div>
          </div>
        </div>
      )}

      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {plans.map((p) => {
          const pending = pendingFor(p.id);
          return (
            <div key={p.id} data-testid={`plan-${p.id}`} className={`relative flex flex-col rounded-2xl border p-6 ${p.popular ? "border-terracotta bg-white shadow-[0_18px_50px_rgba(255,75,10,0.18)]" : "border-border bg-white"}`}>
              {p.popular && <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-terracotta px-3 py-1 text-xs font-bold text-white">Populaire</span>}
              <div className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">{p.name}</div>
              <div className="mt-3 flex items-end gap-1">
                <span className="font-display text-3xl font-extrabold">{p.price.toLocaleString("fr-FR")}</span>
                <span className="mb-1 text-sm text-muted-foreground">FCFA/{p.period}</span>
              </div>
              <ul className="mt-5 flex-1 space-y-2.5">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-ink/75"><CheckCircle weight="fill" className="mt-0.5 shrink-0 text-forest" size={16} /> {f}</li>
                ))}
              </ul>
              {pending ? (
                <div data-testid={`pending-${p.id}`} className="mt-6 flex items-center justify-center gap-2 rounded-full bg-gold/15 px-3 py-2.5 text-sm font-semibold text-[#8a6d1e]">
                  <Clock weight="fill" /> Demande en attente de validation
                </div>
              ) : (
                <Button data-testid={`subscribe-${p.id}`} onClick={() => setDialog(p)}
                  className={`mt-6 w-full rounded-full ${p.popular ? "bg-terracotta text-white hover:bg-[#E03E00]" : "border-2 border-forest bg-transparent text-forest hover:bg-forest hover:text-white"}`}>
                  <CrownSimple weight="fill" className="mr-1.5" /> Demander l'abonnement
                </Button>
              )}
            </div>
          );
        })}
      </div>

      <div className="rounded-2xl border border-border bg-white p-6">
        <div className="flex items-center gap-2"><Ticket weight="fill" className="text-terracotta" size={22} /><h2 className="font-display text-lg font-bold">Code d'activation / coupon</h2></div>
        <p className="mt-1 text-sm text-muted-foreground">Vous avez acheté un coupon en librairie ou auprès d'un ambassadeur ? Activez-le ici.</p>
        <div className="mt-4 flex flex-col gap-3 sm:flex-row">
          <input data-testid="coupon-input" value={coupon} onChange={(e) => setCoupon(e.target.value)} placeholder="Ex : YAYA-DEMO-MOIS"
            className="flex-1 rounded-xl border border-border bg-white px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-terracotta" />
          <Button data-testid="redeem-coupon-btn" onClick={redeem} disabled={redeeming} className="rounded-full bg-forest text-white hover:bg-forest/90">
            {redeeming ? "Activation…" : "Activer le code"}
          </Button>
        </div>
      </div>

      <div className="rounded-2xl border border-dashed border-border bg-white p-6 text-center">
        <Sparkle weight="fill" className="mx-auto text-gold" size={26} />
        <p className="mt-2 text-sm text-muted-foreground">Vous représentez une école, une ONG ou une famille nombreuse ? Des licences dédiées sont disponibles. Contactez-nous sur WhatsApp.</p>
      </div>

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle className="font-display">Souscrire — {dialog?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="rounded-xl bg-cream p-4 text-center">
              <div className="font-display text-2xl font-extrabold">{dialog?.price?.toLocaleString("fr-FR")} FCFA</div>
              <div className="text-sm text-muted-foreground">Abonnement {dialog?.name?.toLowerCase()}</div>
            </div>
            <ol className="space-y-2 text-sm text-ink/75">
              <li className="flex gap-2"><span className="font-bold text-terracotta">1.</span> Cliquez sur le bouton ci-dessous : WhatsApp s'ouvre avec votre demande pré-remplie.</li>
              <li className="flex gap-2"><span className="font-bold text-terracotta">2.</span> Effectuez le paiement Mobile Money via WhatsApp (MTN / Orange).</li>
              <li className="flex gap-2"><span className="font-bold text-terracotta">3.</span> L'administrateur valide et votre abonnement est activé.</li>
            </ol>
            <div className="rounded-xl bg-forest/5 px-4 py-3 text-center text-sm font-semibold text-forest">
              <WhatsappLogo weight="fill" className="mr-1 inline" size={18} /> +237 659 946 824
            </div>
            <Button data-testid="request-whatsapp" onClick={requestPlan} disabled={sending}
              className="w-full rounded-full bg-[#25D366] py-6 text-white hover:bg-[#1FB855]">
              <WhatsappLogo weight="fill" className="mr-2" size={22} /> {sending ? "Ouverture…" : "Demander via WhatsApp"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
